import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';

interface CommandItem {
  id: string;
  label: string;
  icon: string;
  path?: string;
  action?: string;
}

interface CommandGroup {
  group: string;
  items: CommandItem[];
}

const commands: CommandGroup[] = [
  {
    group: 'Navigation',
    items: [
      { id: 'dashboard', label: 'Go to Dashboard', icon: 'bi-house', path: '/' },
      { id: 'accounts', label: 'Chart of Accounts', icon: 'bi-list-ul', path: '/accounts' },
      { id: 'journal', label: 'Journal Entries', icon: 'bi-journal-text', path: '/journal' },
      { id: 'transactions', label: 'Transactions', icon: 'bi-arrow-left-right', path: '/transactions' },
      { id: 'reconciliation', label: 'Bank Reconciliation', icon: 'bi-check2-square', path: '/reconciliation' },
      { id: 'inventory', label: 'Inventory', icon: 'bi-box-seam', path: '/inventory' },
    ],
  },
  {
    group: 'Reports',
    items: [
      { id: 'trial-balance', label: 'Trial Balance', icon: 'bi-table', path: '/reports/trial-balance' },
      { id: 'balance-sheet', label: 'Balance Sheet', icon: 'bi-file-earmark-spreadsheet', path: '/reports/balance-sheet' },
      { id: 'profit-loss', label: 'Profit & Loss', icon: 'bi-graph-up', path: '/reports/profit-loss' },
      { id: 'tax-summary', label: 'Tax Summary', icon: 'bi-receipt', path: '/reports/tax-summary' },
    ],
  },
  {
    group: 'AI Actions',
    items: [
      { id: 'ai-categorize', label: 'Categorize Transactions', icon: 'bi-tags', action: 'categorize' },
      { id: 'ai-reconcile', label: 'Auto-Reconcile', icon: 'bi-magic', action: 'reconcile' },
      { id: 'ai-report', label: 'Generate Report', icon: 'bi-file-earmark-text', action: 'report' },
    ],
  },
];

function CommandItemWrapper({ item, onSelect }: { item: CommandItem; onSelect: (item: CommandItem) => void }) {
  const handleClick = useCallback(() => onSelect(item), [item, onSelect]);
  return (
    <CommandItem onSelect={handleClick}>
      <i className={`bi ${item.icon} mr-2`}></i>
      {item.label}
    </CommandItem>
  );
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleSelect = useCallback((item: CommandItem) => {
    setOpen(false);
    if (item.path) {
      void navigate(item.path);
    } else if (item.action) {
      // Handle AI actions
      console.log('AI action:', item.action);
    }
  }, [navigate]);

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder="Type a command or search..." />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {commands.map((group, idx) => (
          <div key={group.group}>
            <CommandGroup heading={group.group}>
              {group.items.map((item) => (
                <CommandItemWrapper key={item.id} item={item} onSelect={handleSelect} />
              ))}
            </CommandGroup>
            {idx < commands.length - 1 && <CommandSeparator />}
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  );
}